import React, { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, CheckSquare, Square } from 'lucide-react';
import { ChartService } from '../../services/chartService';
import type { Chart } from '@shared/schemas/chart';
import { useAuth } from '../../contexts/AuthContext';
import { currentOrganizationAtom } from '../../store/organizationAtoms';

interface AddChartToDashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChartsSelected: (selectedChartIds: string[]) => void;
  existingChartIdsOnDashboard: string[]; // To disable already added charts
}

const AddChartToDashboardModal: React.FC<AddChartToDashboardModalProps> = ({
  isOpen,
  onClose,
  onChartsSelected,
  existingChartIdsOnDashboard,
}) => {
  const { currentUser } = useAuth();
  const [currentOrganization] = useAtom(currentOrganizationAtom);

  const [availableCharts, setAvailableCharts] = useState<Chart[]>([]);
  const [selectedChartIds, setSelectedChartIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCharts = async () => {
      if (isOpen && currentUser && currentOrganization) {
        setIsLoading(true);
        setError(null);
        try {
          // Assuming ChartService.getUserCharts fetches all charts for the user in the org
          // that are saved to the database.
          const charts = await ChartService.getUserCharts(currentOrganization.id, currentUser.uid);
          setAvailableCharts(charts.filter(chart => !existingChartIdsOnDashboard.includes(chart.id)));
        } catch (err) {
          console.error('Error fetching charts for modal:', err);
          setError('Failed to load charts.');
          setAvailableCharts([]);
        } finally {
          setIsLoading(false);
        }
      } else if (!isOpen) {
        // Reset when dialog is closed
        setAvailableCharts([]);
        setSelectedChartIds(new Set());
        setError(null);
      }
    };

    fetchCharts();
  }, [isOpen, currentUser, currentOrganization, existingChartIdsOnDashboard]);

  const handleToggleChartSelection = (chartId: string) => {
    setSelectedChartIds(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(chartId)) {
        newSelection.delete(chartId);
      } else {
        newSelection.add(chartId);
      }
      return newSelection;
    });
  };

  const handleAddSelectedCharts = () => {
    onChartsSelected(Array.from(selectedChartIds));
    onClose(); // Close the modal after selection
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Charts to Dashboard</DialogTitle>
          <DialogDescription>
            Select the charts you want to add to this dashboard.
          </DialogDescription>
        </DialogHeader>
        
        {isLoading && (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {!isLoading && error && (
          <p className="text-destructive text-center py-4">{error}</p>
        )}

        {!isLoading && !error && availableCharts.length === 0 && (
          <p className="text-muted-foreground text-center py-4">No more charts available to add.</p>
        )}

        {!isLoading && !error && availableCharts.length > 0 && (
          <ScrollArea className="h-[300px] my-4 border rounded-md p-2">
            <div className="space-y-2">
              {availableCharts.map((chart) => (
                <div
                  key={chart.id}
                  className={`flex items-center justify-between p-2 rounded-md cursor-pointer hover:bg-muted/50 transition-colors ${
                    selectedChartIds.has(chart.id) ? 'bg-muted' : ''
                  }`}
                  onClick={() => handleToggleChartSelection(chart.id)}
                >
                  <div className="flex items-center gap-3">
                    {selectedChartIds.has(chart.id) ? 
                      <CheckSquare className="h-5 w-5 text-primary" /> :
                      <Square className="h-5 w-5 text-muted-foreground" />
                    }
                    <span className="font-medium">{chart.title || 'Untitled Chart'}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{chart.type}</span>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          </DialogClose>
          <Button 
            type="button" 
            onClick={handleAddSelectedCharts} 
            disabled={selectedChartIds.size === 0 || isLoading}
          >
            Add {selectedChartIds.size > 0 ? `(${selectedChartIds.size}) ` : ''}Selected
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddChartToDashboardModal; 